import { cn } from "cn";
import Avatar from "@/components/ui/avatar/avatar";
import { Typography } from "@/components/ui/typography";
import { CopyTooltip } from "./common/copy";

export type AccountItem = {
  name?: string | null;
  email?: string | null;
  /** Optional avatar image URL. */
  avatarUrl?: string | null;
};

/** Same size tokens as PlaceItemDisplay / Avatar (xs, sm, base, md, lg). Default "base"; use "md" for dialog/detail. */
type AccountItemSize = "xs" | "sm" | "base" | "md" | "lg";

interface AccountItemDisplayProps {
  /** Account with name and email for display. */
  account: AccountItem | null | undefined;
  /** Avatar size. Default "base"; use "md" for dialog/detail aside. */
  size?: AccountItemSize;
  className?: string;
}

export default function AccountItemDisplay({
  account,
  size = "base",
  className,
}: AccountItemDisplayProps) {
  if (!account) return null;

  return (
    <div
      className={cn(
        /* sorted */
        "flex flex-1 items-center gap-2",
        "min-w-0",
        className,
      )}
      data-size={size}
      data-test="account-item-display"
    >
      <Avatar
        src={account.avatarUrl ?? ""}
        name={account.name ?? ""}
        size={size}
        className="shrink-0"
      />
      <div className="flex flex-col min-w-0 flex-1">
        {account.name ? (
          <Typography
            size="sm"
            className="text-text-dark-max line-clamp-1 truncate"
          >
            {account.name}
          </Typography>
        ) : null}
        {account.email ? (
          <div
            className="flex items-center"
            onClick={(event) => event.stopPropagation()}
          >
            <CopyTooltip copy={account.email ?? ""} className="line-clamp-1">
              <Typography
                size="xs"
                className="break-all text-text-dark-low in-data-[size=xs]:hidden in-data-[size=sm]:hidden"
              >
                {account.email}
              </Typography>
            </CopyTooltip>
          </div>
        ) : null}
      </div>
    </div>
  );
}
